import { SplitPayBadges } from "@/components/PayIn4";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { PACKS, fmt } from "@/data/packs";
import { useCart } from "@/context/CartContext";

const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
    {eyebrow && (
      <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-[var(--oz-gold)] mb-4">
        {eyebrow}
      </span>
    )}
    <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] font-medium tracking-tight text-[var(--oz-text)]">
      {title}
    </h2>
    {subtitle && <p className="mt-4 text-base text-[var(--oz-muted)] leading-relaxed">{subtitle}</p>}
  </div>
);

export const PackSelection = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();

  return (
    <section id="packs" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 md:py-24 scroll-mt-20">
      <SectionHeading
        eyebrow="Choisissez votre pack"
        title="Trouvez le pack qui vous correspond"
        subtitle="Trois formules pensées pour démarrer selon votre budget et votre projet."
      />
      <div data-testid="packs-split-pay-line" className="-mt-6 md:-mt-10 mb-10 md:mb-14 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-[var(--oz-text)]">
        <span className="font-semibold">Payez en 3X ou 4X sans frais</span>
        <span className="font-extrabold italic tracking-tight"><span className="text-[#003087]">Pay</span><span className="text-[#0070BA]">Pal</span> <span className="not-italic font-semibold text-[#003087]">4X</span></span>
        <span className="rounded-md bg-[#FFA8CD] px-1.5 py-0.5 text-xs font-extrabold text-[#17120F]">Klarna. 3X</span>
        <span className="text-xs text-[var(--oz-muted)]">sous réserve d'acceptation</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
        {PACKS.map((pack, idx) => (
          <div
            key={pack.id}
            data-testid={`pack-card-${pack.id}`}
            className={`relative flex flex-col rounded-[1.75rem] overflow-hidden border transition-all duration-300 hover:-translate-y-1.5 oz-fade-up ${
              pack.highlight
                ? "border-[var(--oz-gold)] bg-white shadow-[0_30px_60px_-25px_rgba(198,161,91,0.5)] md:-mt-4 md:mb-4"
                : "border-[var(--oz-border)] bg-white shadow-[0_20px_50px_-30px_rgba(26,23,20,0.35)]"
            }`}
            style={{ animationDelay: `${idx * 0.1}s` }}
          >
            {pack.badge && (
              <div className="absolute top-4 right-4 z-10 bg-[var(--oz-dark)] text-[var(--oz-gold)] text-[10px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full">
                {pack.badge}
              </div>
            )}

            <div className="relative h-52 overflow-hidden">
              <img src={pack.image} alt={pack.name} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
              <span className="absolute bottom-3 left-4 bg-white/90 backdrop-blur text-[var(--oz-text)] text-xs font-semibold px-3 py-1 rounded-full">
                {pack.tagline}
              </span>
            </div>

            <div className="flex flex-col flex-1 p-6 md:p-7">
              <h3 className="font-serif text-2xl text-[var(--oz-text)]">{pack.name}</h3>
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="font-serif text-4xl text-[var(--oz-text)]">{fmt(pack.price)}</span>
                <span className="text-sm text-[var(--oz-muted)]">/ pack</span>
              </div>
              <SplitPayBadges amount={pack.price} className="mt-3" testid={`pack-card-${pack.id}-pay4`} />
              <p className="mt-4 text-sm text-[var(--oz-muted)] leading-relaxed flex-1">{pack.short}</p>

              <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-[var(--oz-text)]">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[var(--oz-nude)] text-[var(--oz-badge)]">
                  <Check className="w-3.5 h-3.5" />
                </span>
                {pack.products} produits inclus
              </div>

              <div className="mt-6 flex flex-col gap-2.5">
                <button
                  data-testid={`pack-card-${pack.id}-select`}
                  onClick={() => addItem(pack.id)}
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm tracking-wide transition-all hover:gap-3 ${
                    pack.highlight
                      ? "bg-[var(--oz-gold)] text-white hover:bg-[var(--oz-gold-hover)]"
                      : "bg-[var(--oz-dark)] text-white hover:bg-black"
                  }`}
                >
                  {pack.cta}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  data-testid={`pack-card-${pack.id}-details`}
                  onClick={() => navigate(`/pack/${pack.slug}`)}
                  className="inline-flex items-center text-sm text-[var(--oz-muted)] hover:text-[var(--oz-gold)] transition-colors py-2 min-h-[40px]"
                >
                  Voir le détail du pack →
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export { SectionHeading };
